'use client';

import { useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';

const isProd =
  process.env.APP_ENV === 'production' || process.env.APP_ENV === 'qa';

/* `urlParent` is filled by the parent app, which sends its own
   `window.location.origin`. It is parsed defensively because a caller could
   send a full url — or nothing at all. */
const getOrigin = (url?: string) => {
  if (!url) {
    return null;
  }

  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

export const useIsAuthorizedToLoadPage = () => {
  const searchParams = useSearchParams();
  // this query param is configured by the acsendo/talent backend
  const urlParent = searchParams.get('urlParent') as string;
  const parentOrigin = getOrigin(urlParent);
  // make the authorized logic required only in production environment
  const [value, setValue] = useState({
    flexSessionId: process.env.FLEX_SESSION_ID,
    isAuthorized: !isProd,
    loading: isProd,
  });

  useEffect(() => {
    if (!isProd) {
      return;
    }

    /**
     * SWAT-1037: the parent answers `getSession` once per listener it has
     * registered, so the same `sessionId` can arrive many times. Every message
     * used to build a brand new state object, which re-rendered this hook's
     * provider — the root layout — even when the session had not changed.
     * When one of those re-renders landed while the router was committing a
     * tab change, react-dom crashed with `Cannot read properties of null
     * (reading 'removeChild')` and the whole microfrontend went down.
     *
     * The listener now (a) only accepts messages coming from the embedding
     * frame, (b) tolerates messages with no `data` (extensions and third-party
     * widgets post those), and (c) keeps the previous state object when the
     * session is already known, so React bails out of the re-render.
     */
    const handleMessage = (message: MessageEvent) => {
      /* the parent sends its own origin in `urlParent`, so a reply from
         anywhere else is not the session handshake. When the origin is
         unknown the message is accepted, to stay as permissive as the
         previous implementation instead of hanging on the loader. */
      if (parentOrigin && message.origin !== parentOrigin) {
        return;
      }

      if (message.data?.event !== 'sessionId') {
        return;
      }

      setValue(currentValue =>
        !currentValue.loading &&
        currentValue.flexSessionId === message.data.value
          ? currentValue
          : {
              loading: false,
              isAuthorized: true,
              flexSessionId: message.data.value,
            },
      );
    };

    window.addEventListener('message', handleMessage);

    /* `postMessage` throws a SyntaxError when the target origin is not a valid
       absolute URL, so an entry point that forgets `urlParent` must not take
       the whole page down with it. The request carries no data, so asking any
       origin is safe — and it keeps the page from hanging on the loader
       forever when `urlParent` is missing. */
    window.parent.postMessage({ event: 'getSession' }, parentOrigin ?? '*');

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [parentOrigin]);

  return value;
};
